import React, {
    useRef,
    useCallback,
    useMemo,
    useState,
    useEffect
} from "react";
import { Bars } from "react-loader-spinner";
import { AgGridReact } from "ag-grid-react";


const CygnusGrid = (props) => {
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef();
    const containerStyle = useMemo(() => ({ width: "100%", height: "auto" }), []);
    const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
    const defaultColDef = useMemo(
        () => ({
            sortable: true,
            filter: true,
            resizable: true,
            animateRows: true,
        }),
        []
    );

    const onGridReady = (params) => {
        setGridApi(params.api);  // ✅ Store API in state
        gridRef.current = params.api; // ✅ Ensure API is also stored in ref
    
        params.api.closeToolPanel();
        console.log("ON GRID READY CALLED!");
    
        setTimeout(() => {
            const allColumnIds = [];
            params.columnApi.getAllColumns().forEach((column) => {
                allColumnIds.push(column.getId());
            });
            params.columnApi.autoSizeColumns(allColumnIds); // Auto-size columns
        }, 100);
    };
    
    const onFirstDataRendered = useCallback((params) => {
        const allColumnIds = [];
        params.columnApi.getAllColumns().forEach((column) => {
            allColumnIds.push(column.getId());
        });

        params.columnApi.autoSizeColumns(allColumnIds); // Auto-size each column based on its own data
    }, []);

    const [columnDefs, setColumnDefs] = useState([]);
    useEffect(() => {
        if (props.columns !== undefined && props.columns !== null && props.columns.length !== 0) {
            setColumnDefs(props.columns)
        }
    }, [props.columns])

    const [rowData, setRowData] = useState([])
    useEffect(() => {
        if (props.rows !== undefined && props.rows !== null && props.rows.length !== 0) {
            setRowData(props.rows)
        }
    }, [props.rows])
    const [preselectedData, setPreselectedData] = useState(props.preselectedrows)
    useEffect(() => {
        if (props.preselectedrows !== undefined && props.preselectedrows !== null && props.preselectedrows !== preselectedData) {
            setPreselectedData(props.preselectedrows)
        }
    }, [props.preselectedrows])

    const [dataIdentifier, setdataIdentifier] = useState(props.dataidentifier)
    useEffect(() => {
        setdataIdentifier(props.dataidentifier)
    }, [props.dataidentifier])

    const selectRows = (ids) => {
        if (!gridApi) return; // ✅ Ensure grid is ready
    
        gridApi.deselectAll(); // ✅ Deselect previous selection
    
        gridApi.forEachNode((node) => {
            if (ids.includes(node.data[dataIdentifier])) {
                node.setSelected(true);
            }
        });
    };
    
    useEffect(() => {
        if (!gridApi || !dataIdentifier || !preselectedData || rowData.length === 0) return; // ✅ Ensure grid & data are ready
    
        console.log("PRESELECTED DATA: ", preselectedData);
        console.log("DATA IDENTIFIER: ", dataIdentifier);
    
        // Convert single object to an array, then map IDs
        const normalizedData = Array.isArray(preselectedData) ? preselectedData : [preselectedData];
        console.log("normalizedData: ", normalizedData);
    
        // Extract valid IDs
        const ids = normalizedData
            .map((item) => item?.[dataIdentifier])
            .filter((id) => id !== undefined && id !== null); // ✅ Ensure IDs are valid
    
        console.log("IDS: ", ids);
    
        if (ids.length > 0) {
            setTimeout(() => { // ✅ Delay selection slightly to ensure grid is fully loaded
                selectRows(ids);
            }, 100);
        }
    }, [preselectedData, dataIdentifier, gridApi, rowData]); // ✅ Runs only when grid & data are ready    

    useEffect(() => {
        console.log("PROPS LOADING GRID: ", props.loading)
        if (gridApi) {
            if (props.loading) {
                gridApi.showLoadingOverlay(); // ✅ Show overlay when API call starts
            } else {
                gridApi.hideOverlay(); // ✅ Hide overlay when API call ends
            }
        }
    }, [props.loading, gridApi]); // ✅ Runs when `loading` changes

    // Update data dynamically
    useEffect(() => {
        if (gridApi) {
            gridApi.applyTransaction({ add: rowData }); // ✅ Use applyTransaction() instead of setRowData()
        }
    }, [gridApi, rowData]); // Runs when gridApi or rowData updates

    const CustomNoRowsOverlay = (props) => {
        return (
            <div
                className="ag-overlay-loading-center flex-row-center-center"
                style={{ height: '9%' }}
            >
                <div className="spinner flex-column-center-center" style={{ fontSize: 14, fontWeight: 500 }}>
                    {props.noRowsMessageFunc()}
                </div>
            </div>
        );
    };
    const noRowsOverlayComponent = useMemo(() => { return CustomNoRowsOverlay }, [rowData]);
    const noRowsOverlayComponentParams = useMemo(() => {
        return {
            noRowsMessageFunc: () =>
                <>{props.noRowOverlayMessage}</>
        }
    }, [rowData]);
    
    const onSelectionChanged = () => {
        if (!gridApi) return; // ✅ Ensure `gridApi` is always available
    
        const selected_rows = gridApi.getSelectedRows();
        props.selectedrows(selected_rows);
    };
    

    /**
     * 
     * @param {*} params 
     */
    const onPaginationChanged = (params) => {
        const currentPage = params.api.paginationGetCurrentPage() + 1; // Current page (1-based index)
        const totalPages = params.api.paginationGetTotalPages(); // Total number of pages
        console.log(`Pagination changed: Current Page = ${currentPage}, Total Pages = ${totalPages}`);
    }

    const customLoadingOverlay = () => {
        return (
            <div
                className="flex-row-center-center"
                style={{ marginTop: "20%" }}
            >
                <div className="spinner flex-row-center-center" style={{ fontSize: 14, fontWeight: 500 }}>
                    <Bars
                        height="80"
                        width="80"
                        color="var(--theme1_primary)"
                        ariaLabel="bars-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                        visible={true}
                    />
                    {props.loadingOverlayMessage}
                </div>
            </div>
        )
    }

    return (
        <div style={containerStyle}>
            <div className="ag-theme-balham" style={gridStyle}>
                <AgGridReact
                    ref={gridRef}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onFirstDataRendered={onFirstDataRendered}
                    onGridReady={onGridReady}
                    suppressRowHoverHighlight={true}
                    domLayout='autoHeight'
                    alwaysShowHorizontalScroll={false}
                    alwaysShowVerticalScroll={false}
                    rowHeight={25}
                    headerHeight={35}
                    rowSelection={props.rowselection}
                    noRowsOverlayComponent={noRowsOverlayComponent}
                    noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                    onSelectionChanged={onSelectionChanged}
                    paginationPageSize={props.pageSize !== undefined && props.pageSize !== null ? props.pageSize : 25} // Page size
                    cacheBlockSize={props.pageSize !== undefined && props.pageSize !== null ? props.pageSize : 25} // Matches page size
                    pagination={true}
                    onPaginationChanged={onPaginationChanged} // Listen for pagination changes
                    components={{ customLoadingOverlay: customLoadingOverlay }} // Register the custom component
                    loadingOverlayComponent="customLoadingOverlay"
                />
            </div>
        </div>
    )
}

export default CygnusGrid;